/**
 * Upgrade Prompt Modal
 * Reusable modal for user-type restriction prompts (resume caps, job limits, AI gating).
 */
import { useRef } from 'react';
import { X, AlertTriangle, Sparkles } from 'lucide-react';
import { useEscapeClose } from '../hooks/useEscapeClose';
import { useFocusTrap } from '../hooks/useFocusTrap';

interface UpgradePromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  limitType: 'resume_soft' | 'resume_hard' | 'job' | 'ai';
  onNavigateToSettings?: () => void;
  onProceed?: () => void;
}

const MODAL_CONTENT: Record<UpgradePromptModalProps['limitType'], {
  icon: 'warning' | 'sparkles';
  title: string;
  body: string;
}> = {
  resume_soft: {
    icon: 'warning',
    title: 'You have quite a few resumes!',
    body: 'Most successful job seekers use one comprehensive resume and let SkillSnap tailor it for each job. Having too many resumes can dilute your focus.',
  },
  resume_hard: {
    icon: 'warning',
    title: 'Resume Limit Reached',
    body: "You've reached the maximum number of resumes. Delete an unused resume to create a new one, or upgrade your plan.",
  },
  job: {
    icon: 'warning',
    title: 'Monthly Job Limit Reached',
    body: "You've used all your job cards for this month. Upgrade to get unlimited job cards.",
  },
  ai: {
    icon: 'sparkles',
    title: 'AI Features Require Subscription',
    body: 'AI-powered resume review and generation require an active subscription.',
  },
};

export function UpgradePromptModal({ isOpen, onClose, limitType, onNavigateToSettings, onProceed }: UpgradePromptModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useEscapeClose(onClose, isOpen);
  useFocusTrap(modalRef, isOpen);

  if (!isOpen) return null;

  const content = MODAL_CONTENT[limitType];
  const IconComponent = content.icon === 'sparkles' ? Sparkles : AlertTriangle;
  const iconColor = content.icon === 'sparkles' ? 'text-purple-500' : 'text-amber-500';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-label={content.title}>
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">{content.title}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="flex items-start gap-3 mb-6">
            <IconComponent className={`w-6 h-6 flex-shrink-0 mt-0.5 ${iconColor}`} />
            <p className="text-sm text-gray-700">{content.body}</p>
          </div>

          <div className="flex justify-end gap-3">
            {limitType === 'resume_soft' && (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg text-sm"
                >
                  Keep Optimizing
                </button>
                <button
                  onClick={() => { onClose(); onProceed?.(); }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
                >
                  Add Another Resume
                </button>
              </>
            )}

            {limitType === 'resume_hard' && (
              <button
                onClick={onClose}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
              >
                Got It
              </button>
            )}

            {limitType === 'job' && (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg text-sm"
                >
                  Got It
                </button>
                <button
                  onClick={() => onNavigateToSettings?.()}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
                >
                  Upgrade
                </button>
              </>
            )}

            {limitType === 'ai' && (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => onNavigateToSettings?.()}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
                >
                  Upgrade
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
